import axios from "axios";
import { GLOBAL_SERVICE_URL, RegionalApiManager } from "src/scenes/App";

export const CHALLENGE_STORAGE_KEY = "checkedIn.emailChallenge";
export const POLL_INTERVAL_MS = 5000;
export const RESEND_INTERVAL_MS = 60000;

export interface Region {
    region: string;
    apiUrl: string;
    imageServiceUrl: string;
    contentBucket: string;
}

export interface EmailChallenge {
    challengeToken: string;
    email: string;
    region: string;
    expiresAt: number;
    resendAt: number;
}

export interface ChallengeStatus {
    verified: boolean;
    requiresProfile: boolean;
}

export interface AppDetails {
    uuid: string;
    token: string;
    appVariant: "checked_in";
    isClient: boolean;
    firstName: string;
    lastName: string;
    email: string;
    companyName: string;
    roleAndPermissions: { permissionIds: string; featureFlags: string[] };
    [key: string]: unknown;
}

export interface NewProfile {
    firstName: string;
    lastName: string;
    acceptToc: true;
}

export async function getRegions(): Promise<Region[]> {
    const response = await axios.get<Region[]>(`${GLOBAL_SERVICE_URL}/regions`);
    return response.data;
}

export async function requestCode(region: Region, email: string): Promise<EmailChallenge> {
    const response = await axios.post<{ challengeToken: string; expiresInSeconds: number }>(
        `${region.apiUrl}/checkedIn/auth/email/request`, { email }
    );
    RegionalApiManager.setRegionData(region);
    return {
        challengeToken: response.data.challengeToken,
        email,
        region: region.region,
        expiresAt: Date.now() + response.data.expiresInSeconds * 1000,
        resendAt: Date.now() + RESEND_INTERVAL_MS
    };
}

export async function challengeStatus(region: Region, challengeToken: string): Promise<ChallengeStatus> {
    const response = await axios.post<ChallengeStatus>(`${region.apiUrl}/checkedIn/auth/email/status`, { challengeToken });
    return response.data;
}

export async function verifyCode(region: Region, challengeToken: string, code: string): Promise<ChallengeStatus> {
    const response = await axios.post<ChallengeStatus>(
        `${region.apiUrl}/checkedIn/auth/email/verify-code`, { challengeToken, code }
    );
    return response.data;
}

export async function authenticate(
    region: Region, challengeToken: string, profile: NewProfile | undefined
): Promise<AppDetails> {
    const response = await axios.post<AppDetails>(
        `${region.apiUrl}/checkedIn/auth/email/authenticate`, { challengeToken, ...profile }
    );
    RegionalApiManager.setRegionData(region);
    return response.data;
}

export function restoreChallenge(): EmailChallenge | null {
    try {
        const challenge: EmailChallenge | null = JSON.parse(sessionStorage.getItem(CHALLENGE_STORAGE_KEY) || "null");
        if (challenge && typeof challenge.challengeToken === "string" && typeof challenge.email === "string"
            && typeof challenge.region === "string" && typeof challenge.resendAt === "number"
            && typeof challenge.expiresAt === "number") {
            return challenge;
        }
    } catch {
        sessionStorage.removeItem(CHALLENGE_STORAGE_KEY);
    }
    return null;
}

export function authError(error: unknown, fallback: string): string {
    if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
            return "Too many attempts. Please wait a minute, then request a new code.";
        }
        if (!error.response) {
            return "We couldn’t connect. Check your connection and try again.";
        }
    }
    return fallback;
}
